/**
 * 서버 설치 파일 — 고객사 서버에 USB 로 가져갈 패키지를 내려받는다.
 *
 * 이 화면의 주인공은 **내려받기 버튼**이다. 받는 사람은 대개 설치를 하러 나가기 직전이고,
 * 알아야 할 것은 셋뿐이다: 어느 판을 받는가(Docker·네이티브), 어느 CPU 인가, 받은 뒤 무엇을 하는가.
 * 그래서 목록을 맨 위에 두고, 고르는 기준과 설치 순서를 바로 아래에 둔다. 자세한 것은 패키지 안의
 * 설치안내(`INSTALL-GUIDE.md`)에 맡긴다.
 *
 * **올리기는 저장소로 직접 간다.** 패키지는 수백 MB 라 API 서버(메모리 한도가 있다)를 거치지 않고
 * 서명 주소로 PUT 한 뒤, 서버가 크기와 SHA-256 을 확인해야 목록에 "받을 수 있음" 으로 선다.
 * 확인 전의 반쪽 파일이 현장에 나가면 설치기가 무결성 검사에서 멈출 때쯤 사람은 이미 고객사에 있다.
 */
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Box, Check, Copy, Download, HardDrive, Trash2, Upload } from 'lucide-react';
import type { ServerPackage, SignageOneClient } from '../api/client.js';
import { Badge, CommandBox, Empty, ErrorNote, formatBytes, Modal, Panel, RelTime } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { copyText } from '../clipboard.js';
import { describeError, useAction, useAsync } from '../hooks.js';
import {
  ARCH_LABEL,
  groupByVersion,
  looksPublicHost,
  parsePackageName,
  PUBLIC_BODY_LIMIT,
  putWithProgress,
  startDownload,
  VARIANT_LABEL,
  purposeLabel,
} from '../serverPackages.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';

const INSTALL_COMMANDS = `cd /media/$USER/*/signageone-server-*      # USB 에 복사한 폴더로
sh install.sh --check                      # 점검만 — 아무것도 바꾸지 않습니다
sh install.sh                              # 설치`;

/**
 * 서버 설치 파일 메뉴의 두 탭 — 받을 파일과, 그것으로 설치하는 법.
 *
 * 매뉴얼을 사이드바의 다른 메뉴로 떼지 않았다. 파일을 받는 사람이 곧 설치하는 사람이고, 받기 전에 "이 서버에
 * 무엇을 부탁해야 하나" 를 알아야 방문 일정을 잡는다.
 */
export function ServerPackagesTabs({ active }: { active: 'files' | 'manual' }): ReactNode {
  const tabs = [
    { id: 'files', label: '설치 파일', to: '/server-packages' },
    { id: 'manual', label: '설치 매뉴얼', to: '/server-packages/manual' },
  ] as const;
  return (
    <nav className="tabs" aria-label="서버 설치 파일">
      {tabs.map((tab) => (
        <Link
          key={tab.id}
          to={tab.to}
          className={`tab ${active === tab.id ? 'is-on' : ''}`}
          aria-current={active === tab.id ? 'page' : undefined}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}

export function ServerPackagesScreen({ client }: { client: SignageOneClient }): ReactNode {
  const list = useAsync(() => client.listServerPackages(), [client]);
  const action = useAction();
  const [uploading, setUploading] = useState(false);
  // 이전 판도 펼친 채로 보인다(2026-10-15 사용자 지시). [이전 판 접기] 로 줄인다.
  const [showOlder, setShowOlder] = useState(true);
  /** 누른 줄 — 서명 주소를 받는 사이 버튼을 한 번 더 누르지 않게. */
  const [fetching, setFetching] = useState<string | null>(null);

  const items = list.data?.items ?? [];
  const groups = groupByVersion(items);

  const download = (item: ServerPackage): void => {
    setFetching(item.id);
    void action
      .run(async () => {
        // 주소는 누를 때마다 새로 받는다 — 목록을 띄워 둔 사이에 만료되지 않게.
        const target = await client.serverPackageDownload(item.id);
        startDownload(target.url, target.filename);
      })
      .finally(() => setFetching(null));
  };

  const remove = async (item: ServerPackage): Promise<void> => {
    const ok = await confirmDialog({
      title: '설치 파일을 지울까요?',
      body: <><strong>{item.filename}</strong> — 저장소의 파일도 함께 지워집니다.</>,
      confirmLabel: '삭제',
      danger: true,
    });
    if (!ok) return;
    void action.run(() => client.deleteServerPackage(item.id), list.reload);
  };

  return (
    <div className="screen animate-fade-in">
      <ServerPackagesTabs active="files" />
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {list.error ? <ErrorNote message={list.error} /> : null}

      <Panel
        title="설치 파일"
        className="screen-fill"
        subtitle={list.data ? `${groups.length}개 판 · ${items.length}개 파일` : undefined}
        actions={
          <button type="button" className="btn" onClick={() => setUploading(true)}>
            <Upload className="h-3.5 w-3.5" strokeWidth={2} />
            설치 파일 올리기
          </button>
        }
      >
        {list.loading && !list.data ? <p className="hint p-4">불러오는 중…</p> : null}
        {list.data && items.length === 0 ? (
          <Empty
            title="아직 올린 설치 파일이 없습니다"
            hint="빌드한 패키지(zip)를 '설치 파일 올리기'로 등록하세요."
          />
        ) : null}
        {groups.length > 0 ? (
          <div className="table-scroll">
            <table className="table min-w-[46rem]">
              <thead>
                <tr>
                  <th>판</th>
                  <th>CPU</th>
                  <th className="col-num">크기</th>
                  <th>
                    SHA-256
                    <HelpTip text="눌러서 전체 값을 복사합니다. 받은 파일을 sha256sum 으로 대조할 때 씁니다." label="SHA-256 도움말" />
                  </th>
                  <th>올린 시각</th>
                  <th className="col-actions" />
                </tr>
              </thead>
              {(showOlder ? groups : groups.slice(0, 1)).map((group, index) => (
                <tbody key={group.version}>
                  <tr className="bg-slate-50/70">
                    <td colSpan={6} className="py-2">
                      <span className="mono font-semibold text-slate-800">{group.version}</span>
                      {index === 0 && group.ready ? (
                        <span className="ml-2">
                          <Badge tone="brand">최신</Badge>
                        </span>
                      ) : null}
                    </td>
                  </tr>
                  {group.items.map((item) => (
                    <tr key={item.id}>
                      <td className="cell-strong">
                        <div className="flex flex-wrap items-center gap-2">
                          {item.edition_label}
                          <span className="font-normal text-slate-500">· {item.variant_label}</span>
                          {item.variant === 'addon' ? <Badge tone="info">이미 깔린 서버용</Badge> : null}
                          {item.status === 'ready' ? null : <Badge tone="warn">올리는 중 · 받을 수 없음</Badge>}
                        </div>
                        {item.notes ? <p className="hint mt-0.5 font-normal">{item.notes}</p> : null}
                      </td>
                      <td className="cell-muted">{item.arch_label}</td>
                      <td className="col-num">{formatBytes(item.size)}</td>
                      <td>{item.sha256 ? <HashCell hash={item.sha256} /> : <span className="cell-muted">—</span>}</td>
                      <td className="cell-muted"><RelTime iso={item.ready_at ?? item.created_at} /></td>
                      <td className="col-actions">
                        <div className="flex items-center justify-end gap-3">
                          {item.status === 'ready' ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-tiny"
                              disabled={fetching === item.id}
                              title={item.filename}
                              onClick={() => download(item)}
                            >
                              <Download className="h-3.5 w-3.5" strokeWidth={2} />
                              내려받기
                            </button>
                          ) : null}
                          <IconButton
                            icon={Trash2}
                            label="지우기"
                            subject={item.filename}
                            danger
                            disabled={action.pending}
                            onClick={() => remove(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
            {/*
              받는 사람이 고르는 것은 거의 언제나 최신 판이다 — 옛 판도 펼친 채로 보이고, [이전 판 접기] 로 줄인다.
            */}
            {groups.length > 1 ? (
              <div className="border-t border-slate-100 px-4 py-2.5">
                <button type="button" className="link" aria-expanded={showOlder} onClick={() => setShowOlder((open) => !open)}>
                  {showOlder ? '이전 판 접기' : `이전 판 ${groups.length - 1}개 보기`}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Panel>

      {/* 처음 한 번 읽는 안내 — 펼친 채로 열고, 읽은 사람은 접는다. */}
      <Panel
        key={list.data ? 'loaded' : 'loading'}
        collapsible
        title="판 고르기와 설치 순서"
        help="두 판 모두 root 권한 없이 설치 사용자의 홈(~/signageone)에 설치됩니다. 포트·백업·업그레이드·제거는 폴더 안 INSTALL-GUIDE.md 에 있습니다."
        actions={
          <Link to="/server-packages/manual" className="btn">
            <BookOpen className="h-3.5 w-3.5" strokeWidth={2} />
            설치 매뉴얼
          </Link>
        }
      >
        {/*
          설치기 자체는 sudo 를 쓰지 않지만, 서버에 따라 관리자만 할 수 있는 준비가 있다. 현장에서 알게 되면
          그날 설치가 끝나지 않는다 — 받기 전에 보이는 자리에 둔다.
        */}
        <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-900">
          서버에 따라 <strong className="font-medium">관리자(sudo) 사전 작업</strong>이 필요합니다(계정, USB 연결, 전용
          Docker, 방화벽).{' '}
          <Link to="/server-packages/manual" className="font-medium underline">
            설치 매뉴얼
          </Link>
          에서 보낼 명령을 만들 수 있습니다.
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-2">
          <VariantCard
            icon={Box}
            title="Docker 판"
            size="약 720MB"
            lines={[
              '운영 서버와 같은 컨테이너 구성(서버 · nginx · MongoDB · MinIO)입니다.',
              'Docker 가 없으면 패키지의 Docker 를 사용자 권한(rootless)으로 설치합니다.',
              'Docker 를 쓸 수 있는 서버에 권장합니다.',
            ]}
          />
          <VariantCard
            icon={HardDrive}
            title="네이티브 판"
            size="약 450MB"
            lines={[
              'Docker 없이 Node.js · MongoDB · MinIO · Caddy 를 사용자 프로세스로 실행합니다.',
              'Docker 를 쓸 수 없는 서버나 저사양 장비(2코어·4GB 안팎)에 적합합니다.',
              '백업 형식이 Docker 판과 같아 판을 바꿔도 데이터를 옮길 수 있습니다.',
            ]}
          />
        </div>

        <div className="border-t border-slate-100 p-4">
          <ol className="list-decimal space-y-1.5 pl-5 text-[13px] text-slate-700">
            <li>
              서버 CPU 에 맞는 zip 을 받습니다(<code className="mono">uname -m</code> 이{' '}
              <code className="mono">x86_64</code> 면 x86_64, <code className="mono">aarch64</code> 면 ARM64).
              <HelpTip
                text="AVX 가 없는 저전력 CPU(Celeron·Pentium Silver·Atom)는 호환 모드(MongoDB 4.4)로 설치됩니다. 이 백업은 호환 모드 서버끼리만 옮길 수 있습니다."
                label="CPU 도움말"
              />
            </li>
            <li>PC 에서 zip 을 풀면 <code className="mono">signageone-server-…</code> 폴더가 나옵니다.</li>
            <li>
              그 폴더를 통째로 USB 에 복사한 뒤 안전하게 제거합니다.
              <HelpTip
                text="exFAT·ext4 를 권장하며 FAT32 도 됩니다(파일은 모두 4GB 미만, 이름은 모두 영문)."
                label="USB 형식 도움말"
              />
            </li>
            <li>
              서비스를 운영할 <strong className="font-medium">일반 사용자</strong>로 로그인해 아래를 실행합니다(root·sudo
              는 거부됩니다).
              <HelpTip
                text="모니터에 연결한 기본 콘솔에서는 한글이 보이지 않아 영어로 안내합니다. 한글로 보려면 SSH 로 접속해 실행하세요."
                label="설치 실행 도움말"
              />
            </li>
          </ol>
          <div className="mt-3">
            <CommandBox command={INSTALL_COMMANDS} />
          </div>
        </div>
      </Panel>

      {uploading ? (
        <UploadPackageModal
          client={client}
          existing={items}
          maxBytes={list.data?.max_bytes ?? 4 * 1024 ** 3 - 1}
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            list.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function VariantCard({
  icon: Icon,
  title,
  size,
  lines,
}: {
  icon: typeof Box;
  title: string;
  size: string;
  lines: string[];
}): ReactNode {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" strokeWidth={1.9} />
        <h3 className="text-[14px] font-semibold text-slate-800">{title}</h3>
        <span className="hint ml-auto">{size}</span>
      </div>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-[12.5px] text-slate-600">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

/** 해시는 앞부분만 보이고, 눌러서 전체를 복사한다 — 받은 파일을 `sha256sum` 으로 대조할 때 쓴다. */
function HashCell({ hash }: { hash: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="link mono inline-flex items-center gap-1"
      title={`${hash}\n눌러서 복사`}
      onClick={(event) => {
        void copyText(hash, event.currentTarget).then(setCopied);
      }}
    >
      {hash.slice(0, 12)}…
      {copied ? <Check className="h-3 w-3" strokeWidth={2} /> : <Copy className="h-3 w-3" strokeWidth={2} />}
    </button>
  );
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'uploading'; loaded: number; total: number }
  | { kind: 'verifying' };

function UploadPackageModal({
  client,
  existing,
  maxBytes,
  onClose,
  onUploaded,
}: {
  client: SignageOneClient;
  existing: readonly ServerPackage[];
  maxBytes: number;
  onClose: () => void;
  onUploaded: () => void;
}): ReactNode {
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const parsed = file ? parsePackageName(file.name) : null;
  const tooBig = file ? file.size > maxBytes : false;
  const replaces = parsed
    ? existing.find(
        (item) =>
          item.version === parsed.version &&
          item.variant === parsed.variant &&
          item.arch === parsed.arch &&
          (item.edition ?? 'standard') === parsed.edition,
      )
    : undefined;
  const publicWarning =
    file !== null && file.size > PUBLIC_BODY_LIMIT && looksPublicHost(window.location.hostname);
  const busy = phase.kind !== 'idle';

  const close = async (): Promise<void> => {
    if (busy) {
      if (phase.kind === 'verifying') return; // 확인은 서버에서 몇 초면 끝난다. 끊으면 줄만 '올리는 중' 으로 남는다.
      const ok = await confirmDialog({
        title: '올리기를 멈추고 닫을까요?',
        body: '올리는 중입니다. 닫으면 처음부터 다시 올려야 합니다.',
        confirmLabel: '멈추고 닫기',
        cancelLabel: '계속 올리기',
        danger: true,
      });
      if (!ok) return;
      abort.current?.abort();
    }
    onClose();
  };

  const submit = async (): Promise<void> => {
    if (!file || !parsed || tooBig) return;
    setError(null);
    const controller = new AbortController();
    abort.current = controller;
    try {
      setPhase({ kind: 'uploading', loaded: 0, total: file.size });
      const created = await client.createServerPackage({ filename: file.name, size: file.size, notes: notes.trim() });
      await putWithProgress(
        created.upload.url,
        file,
        (loaded, total) => setPhase({ kind: 'uploading', loaded, total }),
        controller.signal,
      );
      setPhase({ kind: 'verifying' });
      await client.completeServerPackage(created.package.id);
      onUploaded();
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      setError(describeError(cause));
      setPhase({ kind: 'idle' });
    } finally {
      abort.current = null;
    }
  };

  const percent = phase.kind === 'uploading' && phase.total > 0 ? Math.floor((phase.loaded / phase.total) * 100) : 0;

  return (
    <Modal title="설치 파일 올리기" onClose={close}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field-label">
            패키지(zip)
            <HelpTip
              text="scripts/package-server.mjs 가 만든 zip(signageone-server-…)을 이름 그대로 올립니다. 파일 이름에서 판·종류·CPU 를 읽습니다."
              label="패키지 도움말"
            />
          </span>
          <input
            className="input"
            type="file"
            required
            accept=".zip,application/zip"
            disabled={busy}
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setError(null);
            }}
          />
        </label>

        {file && !parsed ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">
            빌더가 붙인 이름이 아닙니다: <code className="mono">{file.name}</code>. 이름을 바꾼 파일은 올릴 수 없습니다.
          </p>
        ) : null}

        {parsed && file ? (
          <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 rounded-lg border border-slate-200 px-3.5 py-2.5 text-[12.5px]">
            <dt className="text-slate-500">판</dt>
            <dd className="mono">{parsed.version}</dd>
            <dt className="text-slate-500">용도</dt>
            <dd>{purposeLabel(parsed.variant, parsed.edition)}</dd>
            <dt className="text-slate-500">종류</dt>
            <dd>{VARIANT_LABEL[parsed.variant]}</dd>
            <dt className="text-slate-500">CPU</dt>
            <dd>{ARCH_LABEL[parsed.arch]}</dd>
            <dt className="text-slate-500">크기</dt>
            <dd>{formatBytes(file.size)}</dd>
          </dl>
        ) : null}

        {tooBig ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">
            {formatBytes(maxBytes)} 를 넘는 파일은 올릴 수 없습니다.
          </p>
        ) : null}

        {replaces ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            같은 판·종류·CPU 파일이 있습니다. 올리면 <strong className="font-medium">덮어쓰며</strong>, 확인이 끝날
            때까지 내려받을 수 없습니다.
          </p>
        ) : null}

        {publicWarning ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            인터넷 주소로 접속 중이라 <strong className="font-medium">100MB 를 넘는 파일은 중간에서 막힙니다.</strong>{' '}
            사내 주소로 콘솔을 열거나, 서버에서 <code className="mono">scripts/publish-server-package.sh</code> 로
            올리세요.
          </p>
        ) : null}

        <label className="field">
          <span className="field-label">메모 (선택)</span>
          <input
            className="input"
            placeholder="예: 첫 납품판, MongoDB 8.3"
            maxLength={500}
            value={notes}
            disabled={busy}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>

        {phase.kind === 'uploading' ? (
          <div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-th-5 transition-[width] duration-200" style={{ width: `${percent}%` }} />
            </div>
            <p className="hint mt-1.5">
              올리는 중 {percent}% ({formatBytes(phase.loaded)} / {formatBytes(phase.total)}) · 창을 닫지 마세요.
            </p>
          </div>
        ) : null}
        {phase.kind === 'verifying' ? (
          <p className="hint">확인 중… (크기·SHA-256 검사)</p>
        ) : null}

        {error ? <ErrorNote message={error} onDismiss={() => setError(null)} /> : null}

        <div className="form-actions">
          <button type="button" className="btn" onClick={close} disabled={phase.kind === 'verifying'}>
            {phase.kind === 'uploading' ? '멈추기' : '취소'}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !file || !parsed || tooBig}>
            {busy ? '올리는 중…' : '올리기'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
